import { AppShell, Box, Burger, Flex, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import LanguageMenu from 'components/LanguageMenu';
import BuildingSelectorMenu, { SelectableBuilding } from 'components/BuildingSelectorMenu';
import theme from 'theme';
import { HeaderIcon } from 'components/icons/HeaderIcon';
import {
  FAQ_PAGE_PATH,
  HELPDESK_PAGE_PATH,
  HOUSE_PAGE_PATH,
  INSTRUCTIONS_PAGE_PATH,
  MYDOCUMENTS_PAGE_PATH,

  // TICKET_PAGE_PATH,
} from 'routes/paths';
import MenuItem from 'components/MenuItem';
import {
  IconArmchair2,
  IconBook,
  IconChevronRight,
  IconChevronDown,
  IconChevronUp,
  IconHelpHexagon,
  IconMessage,
  IconScript,
  // IconTicket,
} from '@tabler/icons-react';
import useLang from 'lang';
import { useApi } from 'api/api-context';
import { useEffect, useState } from 'react';
import panic from 'panic';
import { CurrentBuilding, SimpleLayoutBuildingContext } from 'layouts/SimpleLayoutContext';

interface SimpleLayoutProps {
  children: React.ReactNode | ((currentBuilding: CurrentBuilding | null) => React.ReactNode);
}

/**
 * Returns the localStorage cache key for a building branding payload.
 *
 * @param {number} currentBuildingId
 * @returns {string}
 */
function getBuildingCacheKey(currentBuildingId: number) {
  return `building_branding_${currentBuildingId}`;
}

/**
 * Returns the cached current building, if one exists for the active building.
 *
 * @param {number | null} currentBuildingId
 * @returns {CurrentBuilding | null}
 */
function getCachedCurrentBuilding(currentBuildingId: number | null): CurrentBuilding | null {
  if (!currentBuildingId) {
    return null;
  }

  const cachedBuilding = localStorage.getItem(getBuildingCacheKey(currentBuildingId));

  if (!cachedBuilding) {
    return null;
  }

  try {
    return JSON.parse(cachedBuilding) as CurrentBuilding;
  } catch {
    localStorage.removeItem(getBuildingCacheKey(currentBuildingId));
    return null;
  }
}

/**
 * SimpleLayout component with dynamic main content area.
 *
 * @param {React.ReactNode} children
 * @returns {React.ReactNode}
 */
function SimpleLayout({ children }: SimpleLayoutProps) {
  const [opened, { toggle }] = useDisclosure();
  const { _t } = useLang();
  const { buildingId, getAction } = useApi();
  const [buildingSelectorOpened, setBuildingSelectorOpened] = useState(false);
  const [currentBuilding, setCurrentBuilding] = useState<CurrentBuilding | null>(() =>
    getCachedCurrentBuilding(buildingId)
  );
  const [buildings, setBuildings] = useState<SelectableBuilding[]>([]);
  const logoUrl = currentBuilding?.logoUrl ?? null;
  const content = typeof children === 'function' ? children(currentBuilding) : children;

  /**
   * Fetch current building data from GetCurrentUser API.
   */
  useEffect(() => {
    if (!buildingId) {
      setCurrentBuilding(null);
      setBuildings([]);
      return;
    }

    setCurrentBuilding(getCachedCurrentBuilding(buildingId));
    localStorage.removeItem(getBuildingCacheKey(buildingId));

    const getCurrentUser = getAction('GetCurrentUser');
    getCurrentUser({})
      .then((response) => {
        const buildings = response.buildings || [];
        const nextCurrentBuilding = buildings.find((building) => building.id === buildingId) ?? null;

        setBuildings(buildings);
        setCurrentBuilding(nextCurrentBuilding);

        if (nextCurrentBuilding) {
          localStorage.setItem(getBuildingCacheKey(buildingId), JSON.stringify(nextCurrentBuilding));
        }
      })
      .catch((error) => {
        panic(error);
        setBuildings([]);
        setCurrentBuilding(null);
      });
  }, [buildingId, getAction]);

  /**
   * Handle opening building selector modal
   */
  const handleOpenBuildingSelector = () => {
    setBuildingSelectorOpened(true);
  };

  /**
   * Reloads the current page with a newly requested building.
   */
  const handleBuildingSelect = (nextBuildingId: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set('buildingId', nextBuildingId.toString());
    window.location.href = url.toString();
  };

  return (
    <SimpleLayoutBuildingContext.Provider value={currentBuilding}>
      <AppShell
        header={{ height: 78 }}
        navbar={{
          width: 300,
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
      >
        <AppShell.Header>
          <Flex direction="row" justify="space-between" align="center" py="0.5rem" px="1rem" h="78px">
            <UnstyledButton aria-label={_t('Select building')} onClick={handleOpenBuildingSelector}>
              <Flex direction="row" align="center" gap="0.5rem" style={{ userSelect: 'none' }}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={_t('Building logo')}
                    style={{ maxWidth: '100px', maxHeight: '64px', objectFit: 'contain' }}
                  />
                ) : (
                  <Box
                    style={{
                      width: '100px',
                      height: '64px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <HeaderIcon />
                  </Box>
                )}

                {buildingSelectorOpened ? (
                  <IconChevronUp size="1.25rem" color={theme.colors?.gray?.[7]} />
                ) : (
                  <IconChevronDown size="1.25rem" color={theme.colors?.gray?.[7]} />
                )}
              </Flex>
            </UnstyledButton>

            <BuildingSelectorMenu
              buildings={buildings}
              currentBuildingId={buildingId}
              opened={buildingSelectorOpened}
              onClose={() => setBuildingSelectorOpened(false)}
              onSelect={handleBuildingSelect}
            />
            <Flex direction="row" align="center" justify="flex-end" w="50%" gap="1rem">
              <Box pr="1.5rem">
                <LanguageMenu />
              </Box>
              <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="md" color={theme.colors?.gray?.[7]} />
            </Flex>
          </Flex>
        </AppShell.Header>

        <AppShell.Navbar py="md" bg={theme.colors?.gray?.[1]}>
          <MenuItem
            to={HOUSE_PAGE_PATH.original}
            icon={IconArmchair2}
            title={_t('My household')}
            description={_t('My license plates, ...')}
            iconRight={IconChevronRight}
            onClick={toggle}
          />

          {/*MyDocuments*/}
          <MenuItem
            to={MYDOCUMENTS_PAGE_PATH.original}
            icon={IconScript}
            title={_t('My Documents')}
            description={_t('List of your documents')}
            iconRight={IconChevronRight}
            onClick={toggle}
          />
          {/*Instructions */}
          <MenuItem
            to={INSTRUCTIONS_PAGE_PATH.original}
            icon={IconBook}
            title={_t('Instructions')}
            description={_t('Instructions for operation and settings of smart devices')}
            iconRight={IconChevronRight}
            onClick={toggle}
          />

          {/*FAQ */}
          <MenuItem
            to={FAQ_PAGE_PATH.original}
            icon={IconHelpHexagon}
            title={_t('FAQ')}
            description={_t('Frequently asked questions')}
            iconRight={IconChevronRight}
            onClick={toggle}
          />
          {/*Helpdesk */}
          {window.location.host === 'phillip-helpdesk.testcreanet.sk' && (
            <MenuItem
              to={HELPDESK_PAGE_PATH.original}
              icon={IconMessage}
              title={_t('Ask')}
              description={_t('Online chat for your questions')}
              iconRight={IconChevronRight}
              onClick={toggle}
            />
          )}
          {/*Ticket */}
          {/* <MenuItem
            to={TICKET_PAGE_PATH.original}
            icon={IconTicket}
            title={_t('Add a ticket')}
            description={_t('Temporary')}
            iconRight={IconChevronRight}
            onClick={toggle}
          /> */}
        </AppShell.Navbar>

        <AppShell.Main bg={theme.colors?.gray?.[1]}>{content}</AppShell.Main>
      </AppShell>
    </SimpleLayoutBuildingContext.Provider>
  );
}
export default SimpleLayout;
